import { Subject, ReplaySubject } from 'rxjs';
import { DATASTRATEGY } from '../constants';
/**
 * @ngdoc service
 * @name proximus.core.entity:EntityStore
 * @description
 * # EntityStoreService
 *  Contains entity CRUD operations responsible for executing them based on entity data strategy and
 *  storage strategy.
 */
export default class EntityStore {
    constructor(halService, entityIdentifier, localEntityStore, remoteEntityStore) {
        this.halService = halService;
        this.entityIdentifier = entityIdentifier;
        this.localEntityStore = localEntityStore;
        this.remoteEntityStore = remoteEntityStore;
        this.saveEntityCallback = (subject, entity) => {
            this.remoteEntityStore
                .saveEntity(entity)
                .then(() => {
                return this.localEntityStore.saveEntityJwts(entity);
            })
                // Note: not using finally since Promise.all is not properly polyfilled?
                .then(() => {
                subject.next(entity);
                subject.complete();
            })
                .catch(() => {
                subject.error(entity);
                subject.complete();
            });
        };
        this.updateEntityCallback = (subject, entity) => {
            this.remoteEntityStore
                .updateEntity(entity)
                .then(() => {
                return this.localEntityStore.saveEntityJwts(entity);
            })
                .then(() => {
                subject.next(entity);
                subject.complete();
            })
                .catch(() => {
                subject.error(entity);
                subject.complete();
            });
        };
        this.deleteEntityCallback = (subject, entity) => {
            this.remoteEntityStore
                .deleteEntity(entity)
                .then(() => {
                subject.next(entity);
                this.localEntityStore.deleteEntity(entity);
                subject.complete();
            })
                .catch(() => {
                subject.error(entity);
                subject.complete();
            });
        };
        this.headEntityCallback = (subject, entity) => {
            this.remoteEntityStore
                .headEntity(entity)
                .then(() => {
                subject.next(entity);
                subject.complete();
            })
                .catch(() => {
                subject.error(entity);
                subject.complete();
            });
        };
        this.cancelEntityCallback = (subject, entity) => {
            this.remoteEntityStore
                .cancelEntityRequest(entity)
                .then(() => {
                subject.next(entity);
                subject.complete();
            })
                .catch(() => {
                subject.error(entity);
                subject.complete();
            });
        };
        this.getEntityCallback = (subject, entity) => {
            this.remoteEntityStore.getEntity(entity).then(() => {
                entity.errorData = null;
                this.localEntityStore
                    .saveEntity(entity)
                    .then(() => {
                    subject.next(entity);
                    subject.complete();
                })
                    .catch(error => {
                    console.error('error saving to ' + entity.storageStrategy + ' = ' + error);
                    subject.error(entity);
                    subject.complete();
                });
            }, rejectedEntity => {
                if (rejectedEntity.value) {
                    this.localEntityStore.saveEntity(rejectedEntity).then(() => {
                        if (rejectedEntity.dataStrategy === DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS ||
                            rejectedEntity.dataStrategy === DATASTRATEGY.PROXY_STORAGE) {
                            subject.error(rejectedEntity);
                        }
                        subject.complete();
                    }, error => {
                        console.error('error saving to ' + rejectedEntity.storageStrategy + ' = ' + error);
                        subject.complete();
                    });
                }
                else {
                    subject.error(rejectedEntity);
                    subject.complete();
                }
            });
        };
    }
    getEntityData(entity) {
        const subject = new ReplaySubject();
        this.localEntityStore.getEntity(entity).then(localStoreEntity => {
            EntityStore.setLocalStoreDataOnEntity(entity, localStoreEntity);
            if (entity.dataStrategy !== DATASTRATEGY.PROXY_STORAGE) {
                EntityStore.setLocalStoreEntityDataToSubject(subject, entity);
            }
            if (EntityStore.entityRequiresUpdate(localStoreEntity, entity)) {
                this.processEntityAction(subject, entity, this.getEntityCallback);
            }
            else {
                subject.complete();
            }
        }, () => {
            if (entity.dataStrategy === DATASTRATEGY.STORAGE_ONLY) {
                subject.error(entity);
                subject.complete();
            }
            else {
                this.processEntityAction(subject, entity, this.getEntityCallback);
            }
        });
        return subject;
    }
    saveEntity(entity) {
        const subject = new Subject();
        this.processEntityAction(subject, entity, this.saveEntityCallback);
        return subject;
    }
    updateEntity(entity) {
        const subject = new Subject();
        this.processEntityAction(subject, entity, this.updateEntityCallback);
        return subject;
    }
    deleteEntity(entity) {
        const subject = new Subject();
        this.processEntityAction(subject, entity, this.deleteEntityCallback);
        return subject;
    }
    headEntity(entity) {
        const subject = new Subject();
        this.processEntityAction(subject, entity, this.headEntityCallback);
        return subject;
    }
    cancelEntityRequest(entity) {
        const subject = new Subject();
        this.processEntityAction(subject, entity, this.cancelEntityCallback);
        return subject;
    }
    static entityRequiresUpdate(localStoreEntity, entity) {
        return (!localStoreEntity.expires ||
            (localStoreEntity.expires <= new Date().getTime() &&
                (entity.dataStrategy === DATASTRATEGY.TWO_PHASE_COMMIT ||
                    entity.dataStrategy === DATASTRATEGY.CACHE_NETWORK_NO_ERRORS ||
                    entity.dataStrategy === DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS)) ||
            entity.dataStrategy === DATASTRATEGY.PROXY_STORAGE);
    }
    processEntityAction(subject, entity, entityActionFunction) {
        if (entity.url === undefined) {
            if (entity.parentRel) {
                this.getParentEntity(entity)
                    .then(parentEntity => {
                    entity.url = this.halService.getEntityUrlFromParentEntity(parentEntity, entity);
                    entityActionFunction(subject, entity);
                })
                    .catch(error => {
                    console.error('error getting parent entity to fetch url for ' + entity, error);
                    subject.error(entity);
                    subject.complete();
                });
            }
            else {
                console.error('neither entity url, neither parentRel is configured in entity config', entity);
                subject.error(entity);
                subject.complete();
            }
        }
        else {
            entityActionFunction(subject, entity);
        }
    }
    getParentEntity(entity) {
        const parentEntityConfig = this.getParentEntityConfiguration(entity);
        return this.localEntityStore.getEntity(parentEntityConfig);
    }
    getParentEntityConfiguration(entity) {
        const cloneParentEntityConfig = Object.assign({}, entity);
        cloneParentEntityConfig.rel = entity.parentRel;
        cloneParentEntityConfig.storageStrategy = entity.parentStorageStrategy;
        cloneParentEntityConfig.dataStrategy = DATASTRATEGY.STORAGE_PROXY;
        cloneParentEntityConfig.uniqueKey = this.entityIdentifier.getUniqueKey(cloneParentEntityConfig);
        return cloneParentEntityConfig;
    }
    static setLocalStoreDataOnEntity(entity, localStoreEntity) {
        entity.value = localStoreEntity.value;
        entity.status = localStoreEntity.status;
        entity.fetchedOn = localStoreEntity.fetchedOn;
        entity.fromStorage = true;
        if (!entity.disableCache) {
            entity.etag = localStoreEntity.etag;
        }
    }
    static setLocalStoreEntityDataToSubject(subject, entity) {
        subject.next(entity);
    }
}
